// ---- Enums ----

import { UpdatePatientEnrollmentRequest } from "@clients/velys-infrastructure/api";

export enum CarePlanScope {
  GLOBAL = "GLOBAL",
  ORG = "ORG",
}

export enum CarePlanStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  SCAN_FAILED = "SCAN_FAILED",
  ARCHIVED = "ARCHIVED",
}

export enum TemplateScope {
  GENERIC = "GENERIC",
  BESPOKE = "BESPOKE",
}

export enum FileAction {
  UPLOAD = "Upload",
  DOWNLOAD = "Download",
}

export type UploadStatus =
  | "idle"
  | "creating"
  | "subscribing"
  | "uploading"
  | "scanning"
  | "processing"
  | "active"
  | "archived"
  | "failed";

// ---- Inputs ----

export interface ListCarePlansInput {
  scope: CarePlanScope;
  orgId?: string;
  limit?: number;
  nextToken?: string;
}

export interface CreateCarePlanInput {
  scope: CarePlanScope;
  name: string;
  version: string;
  orgId?: string;
  carePlanId?: string;
  description?: string;
  templateScope?: TemplateScope;
  patientId?: string;
}

export interface RequestCarePlanUrlInput {
  action: FileAction;
  carePlanId: string;
  orgId?: string;
}

export interface UpdateCarePlanInput {
  carePlanId: string;
  orgId?: string;
  name?: string;
  description?: string;
  version?: string;
}

// ---- Types ----

export interface CarePlan {
  carePlanId: string;
  scope: CarePlanScope;
  orgId?: string;
  status: CarePlanStatus;
  templateScope?: TemplateScope;
  name: string;
  description?: string;
  version: string;
  patientId?: string;
  fileKey?: string;
  fileSizeBytes?: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
  archivedBy?: string;
}

export interface ListCarePlansResponse {
  listCarePlans: {
    items: CarePlan[];
    nextToken?: string;
  };
}

export interface GetCarePlanResponse {
  getCarePlan: CarePlan;
}

export interface RequestCarePlanUrlResponse {
  requestCarePlanUrl: {
    url: string;
    carePlanId: string;
    uploadKey?: string;
    uploadHeaders?: string | null;
  };
}

export interface CreateCarePlanResponse {
  createCarePlan: {
    carePlan: CarePlan;
    uploadUrl: string;
    uploadKey: string;
    uploadExpiresAt: string;
    uploadHeaders: string | null;
  };
}

export interface CarePlanStatusEvent {
  carePlanId: string;
  scope: CarePlanScope;
  orgId?: string;
  status: CarePlanStatus;
  previousStatus: CarePlanStatus;
  templateScope?: TemplateScope;
  name: string;
  version: string;
  updatedAt: string;
}

export interface UpdateCarePlanResponse {
  updateCarePlan: {
    carePlanId: string;
    name: string;
    description?: string;
    version: string;
    updatedAt: string;
  };
}

export interface AdminCareplanTemplate extends UpdatePatientEnrollmentRequest {
  care_plan_name: string;
  email: string;
  timezone: string;
}

// ---- Care Plans Domain Queries & Mutations ----

export const CREATE_CARE_PLAN = `
  mutation CreateCarePlan($input: CreateCarePlanInput!) {
    createCarePlan(input: $input) {
      carePlan {
        carePlanId
        scope
        orgId
        status
        templateScope
        name
        description
        version
        patientId
        createdBy
        createdAt
      }
      uploadUrl
      uploadKey
      uploadExpiresAt
      uploadHeaders
    }
  }
`;

export const LIST_CARE_PLANS = `
  query ListCarePlans($input: ListCarePlansInput!) {
    listCarePlans(input: $input) {
      items {
        carePlanId
        scope
        orgId
        status
        templateScope
        name
        description
        version
        patientId
        fileKey
        fileSizeBytes
        createdBy
        createdAt
        updatedAt
        archivedAt
        archivedBy
      }
      nextToken
    }
  }
`;

export const GET_CARE_PLAN = `
  query GetCarePlan($carePlanId: ID!, $orgId: String) {
    getCarePlan(carePlanId: $carePlanId, orgId: $orgId) {
      carePlanId
      scope
      orgId
      status
      templateScope
      name
      description
      version
      patientId
      fileKey
      fileSizeBytes
      createdBy
      createdAt
      updatedAt
      archivedAt
      archivedBy
    }
  }
`;

export const REQUEST_CARE_PLAN_URL = `
  mutation RequestCarePlanUrl($input: RequestCarePlanUrlInput!) {
    requestCarePlanUrl(input: $input) {
      url
      carePlanId
      uploadKey
      uploadHeaders
    }
  }
`;

export const UPDATE_CARE_PLAN = `
  mutation UpdateCarePlan($input: UpdateCarePlanInput!) {
    updateCarePlan(input: $input) {
      carePlanId
      name
      description
      version
      updatedAt
    }
  }
`;

export const ARCHIVE_CARE_PLAN = `
  mutation ArchiveCarePlan($input: ArchiveCarePlanInput!) {
    archiveCarePlan(input: $input) {
      carePlanId
      status
      archivedAt
      archivedBy
    }
  }
`;

// ---- Care Plans Domain Subscription ----

export const ON_CARE_PLAN_STATUS_CHANGED = `
  subscription OnCarePlanStatusChanged($carePlanId: ID, $orgId: String) {
    onCarePlanStatusChanged(carePlanId: $carePlanId, orgId: $orgId) {
      carePlanId
      scope
      orgId
      status
      previousStatus
      templateScope
      name
      version
      updatedAt
    }
  }
`;
